import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';
import { TOKEN_REFRESH_THRESHOLD_MS } from '@/lib/auth/constants';
import { getTimeUntilExpiry } from '@/lib/auth/tokenDecode';
import { getAccessToken, getLoginMethod, isEmbedMode } from '@/lib/auth/tokenStorage';
import logger from '@/utils/logger';

const MIN_RETRY_MS = 5_000;
const MAX_RETRY_MS = 5 * 60 * 1000;

type UseTokenRefreshOptions = {
  fetchToken: () => Promise<string | null>;
};

// gloss
export const useTokenRefresh = ({ fetchToken }: UseTokenRefreshOptions) => {
  const router = useRouter();
  const fetchTokenRef = useRef(fetchToken);
  useEffect(() => {
    fetchTokenRef.current = fetchToken;
  }, [fetchToken]);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const failCountRef = useRef(0);
  const mountedRef = useRef(true);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const scheduleRefresh = useCallback(() => {
    clearTimer();
    if (!mountedRef.current) return;

    const accessToken = getAccessToken();
    const loginMethod = getLoginMethod();
    if (!accessToken || !loginMethod) return;

    const timeUntilExpiry = getTimeUntilExpiry(accessToken);
    const delay = Math.max(0, timeUntilExpiry - TOKEN_REFRESH_THRESHOLD_MS);

    const retryWithBackoff = () => {
      failCountRef.current++;
      const backoff = Math.min(MIN_RETRY_MS * 2 ** (failCountRef.current - 1), MAX_RETRY_MS);
      timerRef.current = setTimeout(scheduleRefresh, backoff);
      return backoff;
    };

    timerRef.current = setTimeout(async () => {
      if (!mountedRef.current) return;

      try {
        const token = await fetchTokenRef.current();
        if (!mountedRef.current) return;
        if (!token) {
          retryWithBackoff();
          return;
        }
        failCountRef.current = 0;
        scheduleRefresh();
      } catch (error) {
        if (!mountedRef.current) return;
        const backoff = retryWithBackoff();
        logger.error(error, { context: 'useTokenRefresh', attempt: failCountRef.current, nextRetryMs: backoff });
      }
    }, delay);
  }, [clearTimer]);

  useEffect(() => {
    if (typeof window === 'undefined' || isEmbedMode()) return;

    mountedRef.current = true;
    scheduleRefresh();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        failCountRef.current = 0;
        scheduleRefresh();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      mountedRef.current = false;
      clearTimer();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [scheduleRefresh, clearTimer]);

  useEffect(() => {
    if (typeof window === 'undefined' || isEmbedMode()) return;
    scheduleRefresh();
  }, [router.asPath, scheduleRefresh]);
};
